import React, { Suspense, lazy } from 'react'
import LoadingState from './LoadingState'

// HOC для создания lazy компонентов с обработкой ошибок
const withLazyLoading = (importFunc, fallback = null) => {
  const LazyComponent = lazy(importFunc)
  
  return React.forwardRef((props, ref) => (
    <Suspense fallback={fallback || <LoadingState />}>
      <LazyComponent {...props} ref={ref} />
    </Suspense>
  ))
}

// Предзагруженные компоненты для критических частей
const preloadComponent = (importFunc) => {
  const componentImport = importFunc()
  return lazy(() => componentImport)
}

// Компонент для условной загрузки
const ConditionalLazy = ({ 
  condition, 
  component: Component, 
  fallback = null, 
  ...props 
}) => {
  if (!condition) {
    return fallback
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <Component {...props} />
    </Suspense>
  )
}

// Хук для предзагрузки компонентов
const usePreloadComponent = (importFunc) => {
  const [isPreloaded, setIsPreloaded] = React.useState(false)
  
  const preload = React.useCallback(() => {
    if (!isPreloaded) {
      importFunc().then(() => setIsPreloaded(true))
    }
  }, [importFunc, isPreloaded])
  
  return { preload, isPreloaded }
}

// Lazy загрузка админ компонентов
export const LazyAdminDashboard = withLazyLoading(
  () => import('../../pages/admin/Dashboard'),
  <LoadingState message="Загрузка панели управления..." />
)

export const LazyAdminProjects = withLazyLoading(
  () => import('../../pages/admin/Projects'),
  <LoadingState message="Загрузка проектов..." />
)

export const LazyAdminSkills = withLazyLoading(
  () => import('../../pages/admin/Skills'),
  <LoadingState message="Загрузка навыков..." />
)

export const LazyAdminProfile = withLazyLoading(
  () => import('../../pages/admin/Profile'),
  <LoadingState message="Загрузка профиля..." />
)

export const LazyAdminMessages = withLazyLoading(
  () => import('../../pages/admin/Messages'),
  <LoadingState message="Загрузка сообщений..." />
)

export const LazyAdminResume = withLazyLoading(
  () => import('../../pages/admin/Resume'),
  <LoadingState message="Загрузка управления резюме..." />
)

export const LazyLogin = withLazyLoading(
  () => import('../../pages/admin/Login'),
  <LoadingState message="Загрузка формы входа..." />
)

// Lazy загрузка публичных страниц
export const LazyHome = withLazyLoading(
  () => import('../../pages/Home'),
  <LoadingState message="Загрузка главной страницы..." />
)

export const LazyAbout = withLazyLoading(
  () => import('../../pages/About'),
  <LoadingState message="Загрузка страницы о себе..." />
)

export const LazyContact = withLazyLoading(
  () => import('../../pages/Contact'),
  <LoadingState message="Загрузка контактов..." />
)

export const LazyPortfolio = withLazyLoading(
  () => import('../../pages/Portfolio'),
  <LoadingState message="Загрузка портфолио..." />
)

export const LazyResume = withLazyLoading(
  () => import('../../pages/Resume'),
  <LoadingState message="Загрузка резюме..." />
)

export const LazyNotFound = withLazyLoading(
  () => import('../../pages/NotFound'),
  <LoadingState message="Страница не найдена..." />
)

// Lazy загрузка UI компонентов
export const LazyProjectModal = withLazyLoading(
  () => import('../ui/ProjectModal'),
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
    <LoadingState />
  </div>
)

export const LazyParticleBackground = withLazyLoading(
  () => import('../ui/ParticleBackground'),
  null // Фон не критичен, можно загружать без fallback
)

export const LazySkillRadar = withLazyLoading(
  () => import('../ui/SkillRadar'),
  <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
)

export const LazyTechCloud = withLazyLoading(
  () => import('../ui/TechCloud'),
  <div className="w-full h-32 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
)

// Экспорт утилит
export { 
  withLazyLoading, 
  preloadComponent, 
  ConditionalLazy, 
  usePreloadComponent 
}